import {
  MASS_SPECS,
  RECEIVER_RADIUS,
  SOURCE_RADIUS,
  WALL_HALF,
  WORLD_H,
  WORLD_W,
} from '../core/constants';
import { type LevelTrace, receiverNeeds } from '../core/level';
import { mulberry32 } from '../core/rng';
import type { Level, Mass, MassKind } from '../core/types';
import { drawPaper } from './paper';
import { GOLD, GOLD_BRIGHT, INK, beamColor, beamDash, drawColorGlyphs } from './palette';
import { drawMass } from './sprites';
import { type Rect, View } from './view';

export interface DragState {
  kind: MassKind;
  x: number;
  y: number;
  valid: boolean;
  /** Index in `placed` when moving an existing mass; null for a new one from the tray. */
  index: number | null;
}

export interface BoardState {
  level: Level;
  placed: readonly Mass[];
  trace: LevelTrace;
  drag: DragState | null;
  /** Mass index (in placed) with keyboard focus. */
  focus: number | null;
  hints: readonly Mass[];
  /** Time (ms) each telescope last lit up, for the bloom animation; -1 when dark. */
  litAt: readonly number[];
  /** Optional onboarding ghost: animated drag from screen point to world point. */
  ghost?: { from: [number, number]; to: [number, number]; kind: MassKind } | null;
  /** Editor overlays. */
  selection?: { type: string; index: number } | null;
}

export interface RenderOptions {
  time: number;
  reduced: boolean;
  patterns: boolean;
  contrast: boolean;
}

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  max: number;
  color: string;
}

export class BoardRenderer {
  readonly view = new View();
  private ctx: CanvasRenderingContext2D;
  private dpr = 1;
  private w = 0;
  private h = 0;
  private paper: HTMLCanvasElement | null = null;
  private stat: HTMLCanvasElement | null = null;
  private statKey = '';
  private particles: Particle[] = [];
  private level: Level | null = null;
  private contrast = false;

  constructor(private canvas: HTMLCanvasElement) {
    this.ctx = canvas.getContext('2d', { alpha: false })!;
  }

  /** Resize the backing store to the viewport. */
  resize(w: number, hgt: number, dpr: number): void {
    this.w = w;
    this.h = hgt;
    this.dpr = dpr;
    this.canvas.width = Math.round(w * dpr);
    this.canvas.height = Math.round(hgt * dpr);
    this.canvas.style.width = `${w}px`;
    this.canvas.style.height = `${hgt}px`;
    this.paper = null;
    this.statKey = '';
  }

  /** Fit the plate inside a slot (CSS px). */
  setSlot(slot: Rect, allowRotate = true): void {
    const before = `${this.view.plate.x},${this.view.plate.y},${this.view.plate.w},${this.view.rotated}`;
    this.view.fit(slot, allowRotate, 10);
    const after = `${this.view.plate.x},${this.view.plate.y},${this.view.plate.w},${this.view.rotated}`;
    if (before !== after) this.statKey = '';
  }

  setLevel(level: Level | null): void {
    this.level = level;
    this.statKey = '';
    this.particles = [];
  }

  invalidate(): void {
    this.statKey = '';
  }

  get hasParticles(): boolean {
    return this.particles.length > 0;
  }

  /** Raw access for screens that draw their own scenes (title background, thumbnails). */
  get context(): CanvasRenderingContext2D {
    return this.ctx;
  }

  get width(): number {
    return this.w;
  }

  get height(): number {
    return this.h;
  }

  get pixelRatio(): number {
    return this.dpr;
  }

  // ------------------------------------------------------------------------------------------
  // Background and static layers

  private ensurePaper(contrast: boolean) {
    if (this.paper && this.contrast === contrast) return this.paper;
    this.contrast = contrast;
    const c = document.createElement('canvas');
    c.width = Math.max(1, Math.round(this.w * this.dpr));
    c.height = Math.max(1, Math.round(this.h * this.dpr));
    const ctx = c.getContext('2d')!;
    ctx.scale(this.dpr, this.dpr);
    drawPaper(ctx, this.w, this.h, contrast);
    this.paper = c;
    return c;
  }

  /** Paper only (menus, level select). */
  renderPaper(contrast: boolean): void {
    const ctx = this.ctx;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.drawImage(this.ensurePaper(contrast), 0, 0);
  }

  private ensureStatic(opts: RenderOptions, editor: boolean) {
    const level = this.level;
    const key = `${this.w}x${this.h}@${this.dpr}|${opts.contrast}|${opts.patterns}|${editor}|${level ? JSON.stringify([level.walls, level.rocks, level.dust, level.nebulae, level.sources]) : ''}|${this.view.plate.x},${this.view.plate.y},${this.view.scale},${this.view.rotated}`;
    if (this.stat && key === this.statKey) return this.stat;
    const c = this.stat ?? document.createElement('canvas');
    c.width = Math.max(1, Math.round(this.w * this.dpr));
    c.height = Math.max(1, Math.round(this.h * this.dpr));
    const ctx = c.getContext('2d')!;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.drawImage(this.ensurePaper(opts.contrast), 0, 0);
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    this.drawPlate(ctx, opts);
    if (level) {
      ctx.setTransform(...this.view.matrix(this.dpr));
      this.drawNebulae(ctx, level, opts);
      this.drawDust(ctx, level, opts);
      this.drawWalls(ctx, level, opts);
      this.drawRocks(ctx, level, opts);
      ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
      this.drawSources(ctx, level, opts);
    }
    this.stat = c;
    this.statKey = key;
    return c;
  }

  private drawPlate(ctx: CanvasRenderingContext2D, opts: RenderOptions) {
    const p = this.view.plate;
    const v = this.view;
    ctx.save();
    // Slightly deeper exposure inside the plate.
    ctx.fillStyle = opts.contrast ? 'rgba(0,0,0,0.35)' : 'rgba(3, 16, 36, 0.22)';
    ctx.fillRect(p.x, p.y, p.w, p.h);
    // Graticule: faint dotted lines every 100 world units.
    ctx.strokeStyle = opts.contrast ? 'rgba(255,255,255,0.12)' : 'rgba(243,234,211,0.075)';
    ctx.lineWidth = 1;
    ctx.setLineDash([1, 5]);
    for (let x = 100; x < WORLD_W; x += 100) {
      const [ax, ay] = v.toScreen(x, 0);
      const [bx, by] = v.toScreen(x, WORLD_H);
      ctx.beginPath();
      ctx.moveTo(ax, ay);
      ctx.lineTo(bx, by);
      ctx.stroke();
    }
    for (let y = 100; y < WORLD_H; y += 100) {
      const [ax, ay] = v.toScreen(0, y);
      const [bx, by] = v.toScreen(WORLD_W, y);
      ctx.beginPath();
      ctx.moveTo(ax, ay);
      ctx.lineTo(bx, by);
      ctx.stroke();
    }
    ctx.setLineDash([]);
    // Double rule frame with degree ticks, like the border of a star chart.
    const ink = opts.contrast ? '#ffffff' : INK;
    ctx.strokeStyle = ink;
    ctx.globalAlpha = 0.75;
    ctx.lineWidth = 1.2;
    ctx.strokeRect(p.x - 0.5, p.y - 0.5, p.w + 1, p.h + 1);
    ctx.globalAlpha = 0.4;
    ctx.lineWidth = 1;
    const o = 7;
    ctx.strokeRect(p.x - o, p.y - o, p.w + o * 2, p.h + o * 2);
    ctx.globalAlpha = 0.5;
    const tick = (x0: number, y0: number, x1: number, y1: number) => {
      ctx.beginPath();
      ctx.moveTo(x0, y0);
      ctx.lineTo(x1, y1);
      ctx.stroke();
    };
    const step = this.view.scale < 0.6 ? 100 : 50;
    for (let x = step; x < WORLD_W; x += step) {
      const major = x % 200 === 0;
      for (const yy of [0, WORLD_H]) {
        const [sx, sy] = v.toScreen(x, yy);
        const len = major ? o : o * 0.5;
        if (v.rotated) {
          const dir = sx < p.x + p.w / 2 ? -1 : 1;
          tick(sx, sy, sx + dir * len, sy);
        } else {
          const dir = sy < p.y + p.h / 2 ? -1 : 1;
          tick(sx, sy, sx, sy + dir * len);
        }
      }
    }
    for (let y = step; y < WORLD_H; y += step) {
      const major = y % 200 === 0;
      for (const xx of [0, WORLD_W]) {
        const [sx, sy] = v.toScreen(xx, y);
        const len = major ? o : o * 0.5;
        if (v.rotated) {
          const dir = sy < p.y + p.h / 2 ? -1 : 1;
          tick(sx, sy, sx, sy + dir * len);
        } else {
          const dir = sx < p.x + p.w / 2 ? -1 : 1;
          tick(sx, sy, sx + dir * len, sy);
        }
      }
    }
    // Corner ornaments: small engraved stars.
    ctx.globalAlpha = 0.65;
    for (const [cx, cy] of [
      [p.x - o, p.y - o],
      [p.x + p.w + o, p.y - o],
      [p.x - o, p.y + p.h + o],
      [p.x + p.w + o, p.y + p.h + o],
    ] as const) {
      ctx.beginPath();
      for (let k = 0; k < 4; k++) {
        const a = (k / 4) * Math.PI * 2;
        ctx.moveTo(cx, cy);
        ctx.lineTo(cx + Math.cos(a) * 5, cy + Math.sin(a) * 5);
      }
      ctx.stroke();
    }
    ctx.restore();
  }

  private px(n: number) {
    return n / this.view.scale;
  }

  private drawWalls(ctx: CanvasRenderingContext2D, level: Level, opts: RenderOptions) {
    for (const w of level.walls) {
      const ang = Math.atan2(w.y2 - w.y1, w.x2 - w.x1);
      const len = Math.hypot(w.x2 - w.x1, w.y2 - w.y1);
      ctx.save();
      ctx.translate(w.x1, w.y1);
      ctx.rotate(ang);
      const capsule = () => {
        ctx.beginPath();
        ctx.moveTo(0, -WALL_HALF);
        ctx.lineTo(len, -WALL_HALF);
        ctx.arc(len, 0, WALL_HALF, -Math.PI / 2, Math.PI / 2);
        ctx.lineTo(0, WALL_HALF);
        ctx.arc(0, 0, WALL_HALF, Math.PI / 2, (Math.PI * 3) / 2);
        ctx.closePath();
      };
      capsule();
      ctx.fillStyle = opts.contrast ? '#000' : 'rgba(2, 12, 28, 0.78)';
      ctx.fill();
      ctx.save();
      capsule();
      ctx.clip();
      ctx.strokeStyle = opts.contrast ? '#fff' : INK;
      ctx.globalAlpha = 0.55;
      ctx.lineWidth = this.px(0.8);
      const sp = Math.max(this.px(4), 3);
      for (let x = -WALL_HALF * 2; x < len + WALL_HALF * 2; x += sp) {
        ctx.beginPath();
        ctx.moveTo(x, -WALL_HALF);
        ctx.lineTo(x + WALL_HALF * 2, WALL_HALF);
        ctx.stroke();
      }
      ctx.restore();
      capsule();
      ctx.strokeStyle = opts.contrast ? '#fff' : INK;
      ctx.globalAlpha = 0.9;
      ctx.lineWidth = this.px(1.2);
      ctx.stroke();
      ctx.restore();
    }
  }

  private rockPath(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, seed: number) {
    const rand = mulberry32(seed);
    const n = 15;
    const pts: [number, number][] = [];
    const squash = 0.86 + rand.next() * 0.2;
    const tilt = rand.next() * Math.PI;
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2;
      // Lumpy outline that stays close to the collision circle (within about 10 %).
      const lump = 0.92 + rand.next() * 0.16;
      const ell = 1 - (1 - squash) * Math.pow(Math.sin(a - tilt), 2);
      const rr = r * Math.min(1.08, lump * ell + (1 - squash) * 0.5);
      pts.push([x + Math.cos(a) * rr, y + Math.sin(a) * rr]);
    }
    ctx.beginPath();
    pts.forEach(([px, py], i) => {
      const [nx, ny] = pts[(i + 1) % n]!;
      const mx = (px + nx) / 2;
      const my = (py + ny) / 2;
      if (i === 0) ctx.moveTo(mx, my);
      else ctx.quadraticCurveTo(px, py, mx, my);
    });
    const [p0x, p0y] = pts[0]!;
    const [p1x, p1y] = pts[1]!;
    ctx.quadraticCurveTo(p0x, p0y, (p0x + p1x) / 2, (p0y + p1y) / 2);
    ctx.closePath();
  }

  private drawRocks(ctx: CanvasRenderingContext2D, level: Level, opts: RenderOptions) {
    level.rocks.forEach((k, i) => {
      const seed = 101 + i * 31 + Math.round(k.x * 7 + k.y * 13);
      this.rockPath(ctx, k.x, k.y, k.r, seed);
      ctx.fillStyle = opts.contrast ? '#000' : 'rgba(2, 12, 28, 0.8)';
      ctx.fill();
      // Stipple shading lit from the upper left (in world space; fine at this scale).
      ctx.save();
      this.rockPath(ctx, k.x, k.y, k.r, seed);
      ctx.clip();
      const rand = mulberry32(seed + 1);
      const n = Math.round(k.r * k.r * 0.5);
      ctx.fillStyle = opts.contrast ? '#fff' : INK;
      for (let j = 0; j < n; j++) {
        const a = rand.next() * Math.PI * 2;
        const rr = Math.sqrt(rand.next()) * k.r;
        const dx = Math.cos(a) * rr;
        const dy = Math.sin(a) * rr;
        const lit = 0.5 - (dx + dy) / (2.8 * k.r);
        if (rand.next() > lit * 0.75) continue;
        ctx.globalAlpha = 0.5;
        ctx.beginPath();
        ctx.arc(k.x + dx, k.y + dy, this.px(0.65), 0, Math.PI * 2);
        ctx.fill();
      }
      // A couple of pits.
      ctx.globalAlpha = 0.5;
      ctx.strokeStyle = opts.contrast ? '#fff' : INK;
      ctx.lineWidth = this.px(0.8);
      for (let j = 0; j < 2; j++) {
        const a = rand.next() * Math.PI * 2;
        const d = rand.next() * k.r * 0.5;
        ctx.beginPath();
        ctx.arc(
          k.x + Math.cos(a) * d,
          k.y + Math.sin(a) * d,
          k.r * (0.12 + rand.next() * 0.1),
          Math.PI * 0.9,
          Math.PI * 1.9,
        );
        ctx.stroke();
      }
      ctx.restore();
      this.rockPath(ctx, k.x, k.y, k.r, seed);
      ctx.strokeStyle = opts.contrast ? '#fff' : INK;
      ctx.globalAlpha = 0.85;
      ctx.lineWidth = this.px(1.1);
      ctx.stroke();
      ctx.globalAlpha = 1;
    });
  }

  private drawDust(ctx: CanvasRenderingContext2D, level: Level, opts: RenderOptions) {
    level.dust.forEach((d, i) => {
      const g = ctx.createRadialGradient(d.x, d.y, 0, d.x, d.y, d.r);
      g.addColorStop(0, opts.contrast ? 'rgba(0,0,0,0.85)' : 'rgba(1, 8, 20, 0.7)');
      g.addColorStop(0.75, opts.contrast ? 'rgba(0,0,0,0.6)' : 'rgba(1, 8, 20, 0.45)');
      g.addColorStop(1, 'rgba(1, 8, 20, 0.05)');
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc(d.x, d.y, d.r, 0, Math.PI * 2);
      ctx.fill();
      const rand = mulberry32(500 + i);
      ctx.fillStyle = opts.contrast ? '#fff' : INK;
      const n = Math.round(d.r * d.r * 0.09);
      for (let j = 0; j < n; j++) {
        const a = rand.next() * Math.PI * 2;
        const rr = Math.pow(rand.next(), 0.7) * d.r;
        ctx.globalAlpha = 0.12 + rand.next() * 0.25;
        ctx.beginPath();
        ctx.arc(
          d.x + Math.cos(a) * rr,
          d.y + Math.sin(a) * rr,
          this.px(0.5 + rand.next() * 0.7),
          0,
          Math.PI * 2,
        );
        ctx.fill();
      }
      ctx.globalAlpha = 0.45;
      ctx.strokeStyle = opts.contrast ? '#fff' : INK;
      ctx.lineWidth = this.px(1);
      ctx.setLineDash([this.px(1.5), this.px(4)]);
      ctx.beginPath();
      ctx.arc(d.x, d.y, d.r, 0, Math.PI * 2);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.globalAlpha = 1;
    });
  }

  private drawNebulae(ctx: CanvasRenderingContext2D, level: Level, opts: RenderOptions) {
    level.nebulae.forEach((n, i) => {
      const col = beamColor(n.color, opts.contrast);
      const g = ctx.createRadialGradient(n.x, n.y, 0, n.x, n.y, n.r);
      g.addColorStop(0, hexA(col, 0.26));
      g.addColorStop(0.7, hexA(col, 0.13));
      g.addColorStop(1, hexA(col, 0.04));
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2);
      ctx.fill();
      // Wisps.
      const rand = mulberry32(900 + i);
      ctx.strokeStyle = col;
      ctx.lineWidth = this.px(1);
      for (let j = 0; j < 7; j++) {
        const a = rand.next() * Math.PI * 2;
        const rr = n.r * (0.3 + rand.next() * 0.55);
        const sweep = 0.6 + rand.next() * 0.9;
        ctx.globalAlpha = 0.2 + rand.next() * 0.2;
        ctx.beginPath();
        ctx.arc(n.x, n.y, rr, a, a + sweep);
        ctx.stroke();
      }
      ctx.globalAlpha = 0.6;
      ctx.setLineDash([this.px(2), this.px(5)]);
      ctx.beginPath();
      ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.globalAlpha = 1;
      if (opts.patterns) {
        ctx.save();
        ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
        const [sx, sy] = this.view.toScreen(n.x, n.y);
        drawColorGlyphs(ctx, n.color, sx, sy, 8, col);
        ctx.restore();
      }
    });
  }

  private drawSources(ctx: CanvasRenderingContext2D, level: Level, opts: RenderOptions) {
    const s = this.view.scale;
    const r = SOURCE_RADIUS * s;
    for (const src of level.sources) {
      const [x, y] = this.view.toScreen(src.x, src.y);
      const col = beamColor(src.color, opts.contrast);
      const n = Math.max(1, Math.min(3, src.beams ?? 1));
      // Glow.
      const g = ctx.createRadialGradient(x, y, 0, x, y, r * 2.4);
      g.addColorStop(0, hexA(col, 0.35));
      g.addColorStop(1, hexA(col, 0));
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc(x, y, r * 2.4, 0, Math.PI * 2);
      ctx.fill();
      // Emission notches (one per beam).
      for (let k = 0; k < n; k++) {
        const a = this.view.screenAngle(
          ((src.angle + (k - (n - 1) / 2) * (src.spread ?? 0)) * Math.PI) / 180,
        );
        ctx.fillStyle = opts.contrast ? '#fff' : INK;
        ctx.beginPath();
        ctx.moveTo(x + Math.cos(a) * r * 1.65, y + Math.sin(a) * r * 1.65);
        ctx.lineTo(x + Math.cos(a + 0.42) * r * 1.02, y + Math.sin(a + 0.42) * r * 1.02);
        ctx.lineTo(x + Math.cos(a - 0.42) * r * 1.02, y + Math.sin(a - 0.42) * r * 1.02);
        ctx.closePath();
        ctx.fill();
      }
      ctx.fillStyle = '#071a33';
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = opts.contrast ? '#fff' : INK;
      ctx.lineWidth = 1.3;
      ctx.stroke();
      // Lamp: coloured core with engraved rays.
      ctx.fillStyle = col;
      ctx.beginPath();
      ctx.arc(x, y, r * 0.48, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = col;
      ctx.lineWidth = 1;
      for (let k = 0; k < 8; k++) {
        const a = (k / 8) * Math.PI * 2;
        ctx.beginPath();
        ctx.moveTo(x + Math.cos(a) * r * 0.6, y + Math.sin(a) * r * 0.6);
        ctx.lineTo(x + Math.cos(a) * r * 0.82, y + Math.sin(a) * r * 0.82);
        ctx.stroke();
      }
      if (opts.patterns && src.color !== 7) {
        drawColorGlyphs(ctx, src.color, x, y - r * 2.1, Math.max(6, r * 0.55), col);
      }
    }
  }

  // ------------------------------------------------------------------------------------------
  // Frame

  render(state: BoardState | null, opts: RenderOptions, editor = false): void {
    const ctx = this.ctx;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.drawImage(this.ensureStatic(opts, editor), 0, 0);
    if (!state) return;
    const v = this.view;
    const s = v.scale;

    // Masses (fixed first), with field rings for the one being dragged or focused.
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    const all: { m: Mass; fixed: boolean; idx: number }[] = [
      ...state.level.fixed.map((m, i) => ({ m, fixed: true, idx: i })),
      ...state.placed.map((m, i) => ({ m, fixed: false, idx: i })),
    ];
    for (const { m, fixed, idx } of all) {
      if (!fixed && state.drag && state.drag.index === idx) continue;
      const [x, y] = v.toScreen(m.x, m.y);
      if (m.kind === 'hole') this.captureRing(ctx, x, y, MASS_SPECS.hole.mu * Math.E * s, opts);
      if (!fixed && state.focus === idx) this.fieldRings(ctx, m.kind, x, y, opts);
      drawMass(ctx, m.kind, x, y, s, this.dpr);
      if (fixed) this.fixedMark(ctx, m.kind, x, y, opts);
    }
    for (const hnt of state.hints) {
      const [x, y] = v.toScreen(hnt.x, hnt.y);
      this.hintGhost(ctx, hnt.kind, x, y, opts);
    }
    if (state.drag) {
      const d = state.drag;
      const [x, y] = v.toScreen(d.x, d.y);
      this.fieldRings(ctx, d.kind, x, y, opts);
      if (d.kind === 'hole') this.captureRing(ctx, x, y, MASS_SPECS.hole.mu * Math.E * s, opts);
      drawMass(ctx, d.kind, x, y, s, this.dpr, d.valid ? 1 : 0.55);
      if (!d.valid) {
        ctx.save();
        ctx.strokeStyle = '#ff8d72';
        ctx.lineWidth = 1.6;
        ctx.setLineDash([4, 4]);
        ctx.beginPath();
        ctx.arc(x, y, MASS_SPECS[d.kind].radius * s + 6, 0, Math.PI * 2);
        ctx.stroke();
        ctx.restore();
      }
    }

    // Beams.
    this.drawBeams(ctx, state.trace, opts);

    // Telescopes on top of the beams.
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    state.level.receivers.forEach((r, i) => {
      const [x, y] = v.toScreen(r.x, r.y);
      this.drawReceiver(
        ctx,
        r.color,
        receiverNeeds(r),
        state.trace.receivers[i]!,
        x,
        y,
        state.litAt[i] ?? -1,
        opts,
      );
    });

    if (state.ghost && !opts.reduced) this.drawGhost(ctx, state.ghost, opts);
    this.stepParticles(ctx, opts);
  }

  private captureRing(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    r: number,
    opts: RenderOptions,
  ) {
    ctx.save();
    ctx.strokeStyle = opts.contrast ? '#ffd27a' : GOLD;
    ctx.globalAlpha = 0.3;
    ctx.lineWidth = 1;
    ctx.setLineDash([2, 5]);
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
  }

  /** Dotted circles where a passing beam is bent by about 10 and 4 degrees (weak field). */
  private fieldRings(
    ctx: CanvasRenderingContext2D,
    kind: MassKind,
    x: number,
    y: number,
    opts: RenderOptions,
  ) {
    const mu = Math.abs(MASS_SPECS[kind].mu);
    const s = this.view.scale;
    ctx.save();
    ctx.strokeStyle = opts.contrast ? '#fff' : INK;
    ctx.fillStyle = opts.contrast ? '#fff' : INK;
    ctx.lineWidth = 1;
    ctx.font = '600 10px "Atkinson Hyperlegible Next", system-ui, sans-serif';
    ctx.textAlign = 'center';
    for (const [deg, alpha] of [
      [10, 0.42],
      [4, 0.24],
    ] as const) {
      const b = (2 * mu) / ((deg * Math.PI) / 180);
      const r = b * s;
      if (r < MASS_SPECS[kind].radius * s + 6) continue;
      ctx.globalAlpha = alpha;
      ctx.setLineDash([1.5, 4]);
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.globalAlpha = alpha + 0.25;
      ctx.fillText(`${deg}°`, x, y - r - 3);
    }
    ctx.restore();
  }

  private fixedMark(
    ctx: CanvasRenderingContext2D,
    kind: MassKind,
    x: number,
    y: number,
    opts: RenderOptions,
  ) {
    // A small engraved "pin" mark so fixed masses read as part of the plate.
    if (kind === 'hole') return;
    const r = MASS_SPECS[kind].radius * this.view.scale;
    ctx.save();
    ctx.strokeStyle = opts.contrast ? '#fff' : INK;
    ctx.globalAlpha = 0.6;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.arc(x, y, r + 4, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
  }

  private hintGhost(
    ctx: CanvasRenderingContext2D,
    kind: MassKind,
    x: number,
    y: number,
    opts: RenderOptions,
  ) {
    const r = MASS_SPECS[kind].radius * this.view.scale;
    ctx.save();
    ctx.strokeStyle = opts.contrast ? '#ffe24d' : GOLD_BRIGHT;
    ctx.lineWidth = 1.6;
    ctx.setLineDash([3, 3]);
    ctx.beginPath();
    ctx.arc(x, y, r + 3, 0, Math.PI * 2);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.globalAlpha = 0.35;
    drawMass(ctx, kind, x, y, this.view.scale, this.dpr, 0.35);
    ctx.globalAlpha = 0.9;
    ctx.fillStyle = opts.contrast ? '#ffe24d' : GOLD_BRIGHT;
    ctx.font = '700 11px "Atkinson Hyperlegible Next", system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('hint', x, y + r + 16);
    ctx.restore();
  }

  private drawGhost(
    ctx: CanvasRenderingContext2D,
    ghost: NonNullable<BoardState['ghost']>,
    opts: RenderOptions,
  ) {
    // A ghost mass glides from the tray towards a good spot, then fades; repeats.
    const t = (((opts.time / 2600) % 1) + 1) % 1;
    const e = t < 0.7 ? easeInOut(t / 0.7) : 1;
    const fade = t < 0.08 ? t / 0.08 : t > 0.82 ? Math.max(0, 1 - (t - 0.82) / 0.18) : 1;
    const [tx, ty] = this.view.toScreen(ghost.to[0], ghost.to[1]);
    const x = ghost.from[0] + (tx - ghost.from[0]) * e;
    const y = ghost.from[1] + (ty - ghost.from[1]) * e;
    ctx.save();
    ctx.globalAlpha = 0.55 * fade;
    ctx.strokeStyle = INK;
    ctx.setLineDash([3, 5]);
    ctx.lineWidth = 1.2;
    ctx.beginPath();
    ctx.moveTo(ghost.from[0], ghost.from[1]);
    ctx.lineTo(x, y);
    ctx.stroke();
    ctx.restore();
    drawMass(ctx, ghost.kind, x, y, this.view.scale, this.dpr, 0.75 * fade);
    // A fingertip ring.
    ctx.save();
    ctx.globalAlpha = 0.7 * fade;
    ctx.strokeStyle = GOLD_BRIGHT;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(x + 14, y + 16, 9, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
  }

  private drawBeams(ctx: CanvasRenderingContext2D, trace: LevelTrace, opts: RenderOptions) {
    const m = this.view.matrix(this.dpr);
    ctx.setTransform(...m);
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    const px = (n: number) => n / this.view.scale;
    const path = (pts: number[]) => {
      ctx.beginPath();
      ctx.moveTo(pts[0]!, pts[1]!);
      for (let i = 2; i + 1 < pts.length; i += 2) ctx.lineTo(pts[i]!, pts[i + 1]!);
    };
    for (const b of trace.beams) {
      for (const run of b.runs) {
        if (run.pts.length < 4) continue;
        const col = beamColor(run.color, opts.contrast);
        path(run.pts);
        if (!opts.contrast) {
          ctx.strokeStyle = hexA(col, 0.075);
          ctx.lineWidth = px(10);
          ctx.stroke();
          ctx.strokeStyle = hexA(col, 0.16);
          ctx.lineWidth = px(4.5);
          ctx.stroke();
        }
        ctx.strokeStyle = hexA(col, 0.95);
        ctx.lineWidth = px(opts.contrast ? 2.6 : 1.7);
        if (opts.patterns) ctx.setLineDash(beamDash(run.color).map(px));
        ctx.stroke();
        ctx.setLineDash([]);
        if (!opts.reduced) {
          // Shimmer: short bright photons flowing along the beam.
          ctx.strokeStyle = 'rgba(255,255,255,0.55)';
          ctx.lineWidth = px(1.6);
          ctx.setLineDash([px(1.5), px(26)]);
          ctx.lineDashOffset = -px(((opts.time * 0.09) % 27.5) + 0);
          ctx.stroke();
          ctx.setLineDash([]);
          ctx.lineDashOffset = 0;
        }
      }
      // Where a beam is stopped by something solid, a tiny spark.
      if (b.reason === 'blocked' || b.reason === 'absorbed') {
        const col = beamColor(b.runs[b.runs.length - 1]!.color || 7, opts.contrast);
        ctx.fillStyle = hexA(col, 0.5);
        ctx.beginPath();
        ctx.arc(b.x, b.y, px(3), 0, Math.PI * 2);
        ctx.fill();
      }
    }
    ctx.restore();
  }

  private drawReceiver(
    ctx: CanvasRenderingContext2D,
    color: number,
    need: number,
    st: { hits: number; mask: number; lit: boolean },
    x: number,
    y: number,
    litAt: number,
    opts: RenderOptions,
  ) {
    const R = Math.max(9, RECEIVER_RADIUS * this.view.scale);
    const gold = opts.contrast ? '#ffd24d' : GOLD;
    const col = beamColor(color, opts.contrast);
    ctx.save();
    if (st.lit) {
      const age = litAt >= 0 ? opts.time - litAt : 1e9;
      const pulse = opts.reduced ? 0 : Math.max(0, 1 - age / 700);
      const bloomR = R * (2.4 + pulse * 1.6);
      const g = ctx.createRadialGradient(x, y, R * 0.3, x, y, bloomR);
      g.addColorStop(0, hexA(GOLD_BRIGHT, 0.5 + pulse * 0.3));
      g.addColorStop(0.4, hexA(gold, 0.18 + pulse * 0.15));
      g.addColorStop(1, hexA(gold, 0));
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc(x, y, bloomR, 0, Math.PI * 2);
      ctx.fill();
    }
    // Crosshair ticks.
    ctx.strokeStyle = gold;
    ctx.lineWidth = 1.2;
    ctx.globalAlpha = 0.85;
    for (let k = 0; k < 4; k++) {
      const a = (k / 4) * Math.PI * 2 + Math.PI / 4;
      ctx.beginPath();
      ctx.moveTo(x + Math.cos(a) * R * 1.12, y + Math.sin(a) * R * 1.12);
      ctx.lineTo(x + Math.cos(a) * R * 1.38, y + Math.sin(a) * R * 1.38);
      ctx.stroke();
    }
    ctx.globalAlpha = 1;
    // Lens.
    if (st.lit) {
      const g = ctx.createRadialGradient(x - R * 0.2, y - R * 0.2, 0, x, y, R);
      g.addColorStop(0, '#fffaf0');
      g.addColorStop(0.6, GOLD_BRIGHT);
      g.addColorStop(1, gold);
      ctx.fillStyle = g;
    } else {
      // A tinted lens, never pure black, so a dark telescope never reads as a black hole.
      const g = ctx.createRadialGradient(x - R * 0.3, y - R * 0.3, 0, x, y, R);
      g.addColorStop(0, opts.contrast ? 'rgba(255, 210, 77, 0.32)' : 'rgba(232, 195, 106, 0.28)');
      g.addColorStop(1, opts.contrast ? 'rgba(40, 30, 6, 0.9)' : 'rgba(34, 40, 52, 0.92)');
      ctx.fillStyle = g;
    }
    ctx.beginPath();
    ctx.arc(x, y, R, 0, Math.PI * 2);
    ctx.fill();
    // Light already arriving (but not the right mix or not enough beams).
    if (!st.lit && st.hits > 0 && st.mask) {
      ctx.fillStyle = hexA(beamColor(st.mask, opts.contrast), 0.35);
      ctx.beginPath();
      ctx.arc(x, y, R * 0.55, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.strokeStyle = gold;
    ctx.lineWidth = 1.8;
    ctx.beginPath();
    ctx.arc(x, y, R, 0, Math.PI * 2);
    ctx.stroke();
    // Required colour ring.
    ctx.strokeStyle = st.lit ? 'rgba(80, 50, 10, 0.55)' : col;
    ctx.lineWidth = Math.max(1.6, R * 0.13);
    ctx.beginPath();
    ctx.arc(x, y, R * 0.7, 0, Math.PI * 2);
    ctx.stroke();
    // Beams-needed pips.
    if (need > 1) {
      for (let k = 0; k < need; k++) {
        const a = -Math.PI / 2 + (k - (need - 1) / 2) * 0.5;
        const px = x + Math.cos(a) * R * 1.62;
        const py = y + Math.sin(a) * R * 1.62;
        ctx.beginPath();
        ctx.arc(px, py, Math.max(2.4, R * 0.15), 0, Math.PI * 2);
        if (st.hits > k && (st.mask & ~color) === 0) {
          ctx.fillStyle = GOLD_BRIGHT;
          ctx.fill();
        } else {
          ctx.fillStyle = 'rgba(2,10,24,0.9)';
          ctx.fill();
          ctx.strokeStyle = gold;
          ctx.lineWidth = 1.2;
          ctx.stroke();
        }
      }
    }
    // Colour symbols when patterns are on, or for any non-white telescope (never colour alone).
    if (opts.patterns || color !== 7) {
      drawColorGlyphs(ctx, color, x, y, Math.max(5, R * 0.42), st.lit ? '#5a3a08' : col);
    }
    ctx.restore();
  }

  // ------------------------------------------------------------------------------------------
  // Particles

  burst(wx: number, wy: number, color: string, count = 16): void {
    const [x, y] = this.view.toScreen(wx, wy);
    const rand = mulberry32(Math.round(wx * 31 + wy * 17 + performance.now()));
    for (let i = 0; i < count; i++) {
      const a = rand.next() * Math.PI * 2;
      const sp = 40 + rand.next() * 90;
      const max = 500 + rand.next() * 500;
      this.particles.push({
        x,
        y,
        vx: Math.cos(a) * sp,
        vy: Math.sin(a) * sp,
        life: max,
        max,
        color,
      });
    }
  }

  private lastStep = 0;
  private stepParticles(ctx: CanvasRenderingContext2D, opts: RenderOptions) {
    const now = opts.time;
    const dt = this.lastStep ? Math.min(50, now - this.lastStep) : 16;
    this.lastStep = now;
    if (this.particles.length === 0) return;
    if (opts.reduced) {
      this.particles = [];
      return;
    }
    ctx.save();
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    ctx.globalCompositeOperation = 'lighter';
    for (const p of this.particles) {
      p.life -= dt;
      p.x += (p.vx * dt) / 1000;
      p.y += (p.vy * dt) / 1000;
      p.vx *= 0.97;
      p.vy *= 0.97;
      const k = Math.max(0, p.life / p.max);
      ctx.fillStyle = hexA(p.color, 0.8 * k);
      ctx.beginPath();
      ctx.arc(p.x, p.y, 1 + 1.4 * k, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
    this.particles = this.particles.filter((p) => p.life > 0);
  }
}

function easeInOut(t: number) {
  return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
}

/** '#rrggbb' + alpha -> rgba(). */
export function hexA(hex: string, a: number): string {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${Math.max(0, Math.min(1, a)).toFixed(3)})`;
}
